import supabase from './Supabase';
import {
    clearPatreonRefreshOnLogin,
    patreonRefreshOnLoginRequested,
    refreshPatreonUser,
} from './Patreon';
import { parseBenefitsFromRpc } from './Benefits';

let loginPatreonRefreshPromise: Promise<any> | null = null;

export const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
};

/**
 * Attach the already-figured-out benefit map for the signed-in user.
 * Other people's rows (admin looking someone up) skip this.
 */
async function withBenefits(userRow, isSelf) {
    if (!userRow || !isSelf) {
        return userRow;
    }

    const { data: benefits, error } = await supabase.rpc('get_user_benefits', {
        p_user_id: userRow.user_id,
    });

    if (error || !benefits) {
        return userRow;
    }

    return {
        ...userRow,
        benefits: parseBenefitsFromRpc(benefits),
    };
}

export async function getUserInfo(userId) {
    const { data, error } = await supabase
        .from('userData')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

    if (error) {
        return null;
    }

    if (!data) {
        return null;
    }

    const session = await supabase.auth.getSession();
    const isSelf = session.data.session?.user?.id === userId;

    // Login only: if this account is linked, ask Patreon once to confirm they
    // are still linked and to update their stored campaign tier. Concurrent
    // getUserInfo calls (login page + auth listener) share that in-flight
    // request. Ordinary page loads never set the flag, so they skip this.
    if (isSelf && loginPatreonRefreshPromise) {
        const refreshed = await loginPatreonRefreshPromise;
        return refreshed ?? data;
    }

    if (isSelf && patreonRefreshOnLoginRequested()) {
        clearPatreonRefreshOnLogin();

        if (data.patreon_linked) {
            // Linked ≠ patron. Refresh still runs so we can store (or clear)
            // patreon_tier_id from what Patreon currently reports.
            loginPatreonRefreshPromise = (async () => {
                try {
                    await refreshPatreonUser(userId, { silent: true });
                    const { data: refreshed, error: refreshError } = await supabase
                        .from('userData')
                        .select('*')
                        .eq('user_id', userId)
                        .maybeSingle();
                    if (refreshError || !refreshed) {
                        return withBenefits(data, true);
                    }
                    return withBenefits(refreshed, true);
                } finally {
                    loginPatreonRefreshPromise = null;
                }
            })();

            const refreshed = await loginPatreonRefreshPromise;
            return refreshed ?? data;
        }
    }

    return withBenefits(data, isSelf);
}

export async function insertNewUser(userId, email) {
    const { data, error } = await supabase
        .from('userData')
        .insert({
            user_id: userId,
            email: email
        })
        .select()
        .single();

    if (error) {
        console.error(`Error inserting new user with ID ${userId}:`, error);
        return null;
    }

    const session = await supabase.auth.getSession();
    const isSelf = session.data.session?.user?.id === userId;

    // New accounts start on site defaults (no Patreon tier, no local overrides).
    return withBenefits(data, isSelf);
}

export async function updateUserStrikes(userId, email, strikeReason, strikeType = "Temporary") {
    if (!userId) {
        return null;
    }

    const today = new Date();
    const strikeDate = today.getFullYear() + '-' +
        String(today.getMonth() + 1).padStart(2, '0') + '-' +
        String(today.getDate()).padStart(2, '0');

    const { data: insertData, error: insertError } = await supabase
        .from('user_strikes')
        .upsert({
            user_id: userId,
            email: email,
            strike_reason: strikeReason,
            strike_date: strikeDate,
            strike_type: strikeType
        })
        .select('*')
        .maybeSingle();

    if (insertError) {
        console.error(`Error updating user ${email}:`, insertError);
        return null;
    }

    return insertData;
}

export async function deleteAccount() {
    const session = await supabase.auth.getSession();
    if (!session.data.session) {
        return false;
    }

    const { data, error } = await supabase.functions.invoke('account-utility', {
        body: {
            action: 'delete_account',
            user_session: session.data.session.access_token,
        }
    });

    if (error || !data) {
        console.error('Error deleting account:', error);
        return false;
    }

    return true;
}

export async function blacklistUser(userId) {
    if (!userId) {
        return null;
    }

    const { data, error } = await supabase
        .from('userData')
        .update({ isBlacklisted: true })
        .eq('user_id', userId)
        .select()
        .maybeSingle();

    if (error) {
        console.error(`Error updating user:`, error);
        return null;
    }

    return data;
}